import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit, Plus } from 'lucide-react';
import { stcBinGrpService, StcBinGrpDto, UpdateStcBinGrpRequest } from '../../../../../services/stcBinGrpService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { WarehouseTable, useInlineEdit } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction } from '../../../../shared/table/types/table';
import { BinGrpSidePanel } from './BinGrpSidePanel';
import { BinGrpCreateModal } from './BinGrpCreateModal';

interface BinGrpDataTableProps {
  refreshTrigger?: number;
}

export const BinGrpDataTable: React.FC<BinGrpDataTableProps> = ({
  refreshTrigger
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const companyNo = user?.companyNo || '';

  // Core data state
  const [data, setData] = useState<StcBinGrpDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Modal state
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Inline editing
  const inlineEditConfig = useInlineEdit<StcBinGrpDto>({
    getRowId: (row) => row.id,
    onSave: async (_originalRow, editedData) => {
      const request: UpdateStcBinGrpRequest = {
        binGrpNo: editedData.binGrpNo as number | undefined,
        description1: (editedData.description1 as string) || '',
        commonAvailable: (editedData.commonAvailable as boolean) || false,
        externalSystemID: editedData.externalSystemID as number | undefined
      };
      await stcBinGrpService.update(
        _originalRow.companyNo,
        _originalRow.binGrpNo,
        request
      );
    },
    onSaveSuccess: () => loadData(),
    onSaveError: (err) => setError(err.message || t('manager.modules.adminBins.common.errors.saveFailed', {}, 'Feil ved lagring'))
  });

  // Load data
  useEffect(() => {
    loadData();
  }, [companyNo]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadData();
    }
  }, [refreshTrigger]);

  const loadData = useCallback(async () => {
    if (!companyNo) return;

    setIsLoading(true);
    setError(null);

    try {
      const binGrpData = await stcBinGrpService.getCurrent().catch(() =>
        stcBinGrpService.getAll().catch(() => [])
      );
      setData(binGrpData);
    } catch (err: any) {
      console.error('Error loading bin group data:', err);
      setError(t('manager.modules.adminBins.binGrp.loadError', {}, 'Kunne ikke laste lokasjonsgrupper. Prøv igjen.'));
    } finally {
      setIsLoading(false);
    }
  }, [companyNo, t]);

  // Column factory
  const columnFactory = createColumnFactory<StcBinGrpDto>();

  // Column definitions
  const columns: ColumnDefinition<StcBinGrpDto>[] = useMemo(() => [
    columnFactory.createNumberColumn('binGrpNo', 'binGrpNo', t('manager.modules.adminBins.binGrp.fields.groupNo', {}, 'Gruppenummer'), {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),

    columnFactory.createTextColumn('description1', 'description1', t('common.fields.description', {}, 'Beskrivelse'), {
      minWidth: 250,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('commonAvailable', 'commonAvailable', t('manager.modules.adminBins.common.commonAvailable', {}, 'Felles tilgjengelig'), {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('externalSystemID', 'externalSystemID', t('manager.modules.adminBins.common.externalSystemId', {}, 'Ekstern system ID'), {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    })
  ], [t]);

  // Handle bulk edit
  const handleBulkEdit = useCallback(() => {
    if (selectedIds.length === 0) {
      alert(t('manager.modules.adminBins.binGrp.selectAtLeastOne', {}, 'Velg minst én lokasjonsgruppe for å redigere'));
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedIds.length, t]);

  // Table actions
  const tableActions: TableAction<StcBinGrpDto>[] = useMemo(() => [
    {
      id: 'create',
      label: t('manager.modules.adminBins.binGrp.create.title', {}, 'Ny lokasjonsgruppe'),
      icon: Plus,
      onClick: () => setShowCreateModal(true),
      variant: 'primary' as const
    },
    {
      id: 'bulk-edit',
      label: t('manager.modules.adminBins.common.maintain', { '0': selectedIds.length }, `Vedlikehold (${selectedIds.length})`),
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: () => selectedIds.length === 0,
      variant: 'primary' as const
    }
  ], [selectedIds.length, handleBulkEdit, t]);

  // Build selected rows map for BinGrpSidePanel compatibility
  const selectedRowsMap = useMemo(() => {
    const map: { [key: string]: boolean } = {};
    selectedIds.forEach(id => { map[String(id)] = true; });
    return map;
  }, [selectedIds]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={data}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'bin-groups-table',
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            }
          }}
          selectedRows={selectedIds}
          onSelectionChange={(ids) => setSelectedIds(ids as number[])}
          getRowId={(item) => item.id}
          tableActions={tableActions}
          inlineEditConfig={inlineEditConfig}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {/* Bulk Edit Side Panel */}
      {showBulkEditModal && selectedIds.length > 0 && (
        <BinGrpSidePanel
          selectedRows={selectedRowsMap}
          data={data}
          onClose={() => setShowBulkEditModal(false)}
          onSave={() => {
            loadData();
            setSelectedIds([]);
            setShowBulkEditModal(false);
          }}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <BinGrpCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            loadData();
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};
